import fetch from '../utils/fetch';
import busyActionFactory from '../busy';
import { emailURL } from 'endpoints';
import { Intent } from '@blueprintjs/core';

import {
    ADD_EMAIL_COMPONENT,
    UPDATE_EMAIL_PROPS,
    UPDATE_INPUTS,
    UPDATE_PROPS,
    REMOVE_COMPONENT,
    UPDATE_SELECTED_EMAIL,
    SAVE_EMAIL_RESPONSE
} from './builder';



/*******************************************************************************
 *
 *   1. Default State
 *   2. Action Types
 *   3. Action Creators
 *   4. Reducers
 *
 */

/*******************************************************************************
 *  1. Default State
 */

export const initialState = {
    loading: false,
    loadingError: '',
    configHasChanged: false,
    availableComponents: [],
    availableLayouts: [],
    savedEmails: [],
    locales: [],
    types: [
        {
            'code': 'recipient',
            'label': 'Recipient'
        }, {
            'code': 'singleLevelManager',
            'label': 'Single Level Manager'
        }, {
            'code': 'multiLevelManager',
            'label': 'Multi Level Manager'
        }, {
            'code': 'reminder',
            'label': 'Reminder'
        }, {
            'code': 'expiration',
            'label': 'Expiration'
        }, {
            'code': 'contributory',
            'label': 'Contributory'
        }, {
            'code': 'contact',
            'label': 'Contact Us'
        }, {
            'code': 'celebration',
            'label': 'Celebration'
        }
    ],
    products: [
        {
            'code': 'contests',
            'label': 'Contests General'
        },
        {
            'code': 'contests-webapp',
            'label': 'Contests Web App'
        },
        {
            'code': 'contests-native-app',
            'label': 'Contests Native App'
        },
        {
            'code': 'service-awards-webapp',
            'label': 'Service Awards Web App'
        },
        {
            'code': 'gift-code-webapp',
            'label': 'Gift Code Web App'
        }
    ],
    preview: {
        html: ''
    },
    toasts: []
};

// Some default toast objects
const infoToast = {
    message: '',
    intent: Intent.PRIMARY,
    icon: 'info-sign'
};

const successToast = {
    message: '',
    intent: Intent.SUCCESS,
    icon: 'tick'
};

const errorToast = {
    message: '',
    intent: Intent.DANGER,
    icon: 'warning-sign',
    timeout: 0
};

/*******************************************************************************
 *  2. Action Types
 */

export const DATA_LOADING = 'DATA_LOADING';
export const DATA_LOADING_ERROR = 'DATA_LOADING_ERROR';
export const LOAD_DATA = 'LOAD_DATA';
export const BUSY_FETCHING_EMAIL = 'BUSY_FETCHING_EMAIL';
export const LOAD_PREVIEW = 'LOAD_PREVIEW';
export const BUSY_FETCHING_PREVIEW = 'BUSY_FETCHING_PREVIEW';
export const SET_TOAST = 'state/setToast';
export const CLEAR_TOAST = 'state/clearToast';
/*******************************************************************************
 *  3. Action Creators
 */

export const loading = ( loading ) => {
    return {
        type: DATA_LOADING,
        loading
    };
};

export const loadingError = ( error ) => {
    return {
        type: DATA_LOADING_ERROR,
        error
    };
};

export const loadData = ( data ) => {
    return  {
        type: LOAD_DATA,
        data
    };
};

export const loadPreview = ( data ) => {
    return {
        type: LOAD_PREVIEW,
        data
    };
};

export const clearPreview = () => {
    return {
        type: LOAD_PREVIEW,
        data: {
            preview: {
                html: ''
            }
        }
    };
};

export const setToast = ( message, toastType ) => ( { type: SET_TOAST, message, toastType } );

export const clearToast = ( ) => ( { type: CLEAR_TOAST } );

export function getAppData() {
    return async ( dispatch, getState ) => {

        dispatch( loading( true ) );

        try {
            const res = {
                availableComponents: [],
                availableLayouts: [],
                locales: [],
                savedEmails: [],
                errors: null
            };

            const [ componentsRequest, layoutsRequest, emailsRequest, localesRequest ] = await Promise.all( [
                fetch( `${ emailURL }/components `, {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                } ),
                fetch( `${ emailURL }/layouts`, {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                } ),
                fetch( `${ emailURL }`, {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                } ),
                fetch( `${ emailURL }/locales`, {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                } )
            ] );

            const componentsResponse = await componentsRequest.json();
            const layoutsResponse = await layoutsRequest.json();
            const emailsResponse = await emailsRequest.json();
            const localesResponse = await localesRequest.json();

            if ( componentsResponse.success ) {
                res.availableComponents = componentsResponse.components;
                dispatch( loadData( res ) );
            } else {
                res.errors.push( componentsResponse.errors );
                dispatch( loadingError( res.errors ) );
            }

            if ( layoutsResponse.success ) {
                res.availableLayouts = layoutsResponse.layouts;
                dispatch( loadData( res ) );
            } else {
                res.errors.push( layoutsResponse.errors );
                dispatch( loadingError( res.errors ) );
            }

            if ( localesResponse.success ) {
                res.locales = localesResponse.locales.map( locale => {
                    return {
                        value: locale.code,
                        label: `${ locale.label_enUS } - ${ locale.label }`
                    };
                } ).sort( ( a, b ) => a.label.localeCompare( b.label ) );
                dispatch( loadData( res ) );
            } else {
                res.errors.push( localesResponse.errors );
                dispatch( loadingError( res.errors ) );
            }

            if ( emailsResponse.success ) {
                res.savedEmails = emailsResponse.emails;
                dispatch( loadData( res ) );
            } else {
                res.errors.push( emailsResponse.errors );
                dispatch( loadingError( res.errors ) );
            }

            dispatch( loading( false ) );
            return res;
        } catch ( error ) {
            dispatch( loadingError( error ) );
            dispatch( loading( false ) );
        }

    };
}

const busyFetchingPreview = busyActionFactory( BUSY_FETCHING_PREVIEW );
export const fetchPreview = () => {

    return ( dispatch, getState ) => {

        busyFetchingPreview( true );

        const previewRequest = {
            layout: getState().builder.layout,
            product: getState().builder.product,
            name: getState().builder.name,
            type: getState().builder.type,
            components: getState().builder.components.map( component => {
                return {
                    name: component.name,
                    namespace: component.namespace,
                    props: Object.keys( component.props ).reduce( ( flatProps, propKey ) => {
                        const input = getState().builder.inputs.find( input => input.id === component.props[ propKey ].inputId );
                        flatProps[ propKey ] = input ? `\${ref:inputs.${ input.code }}` : '';
                        return flatProps;
                    }, {} )
                };
            } ),
            inputs: getState().builder.inputs.reduce( ( flatInputs, current ) => {
                flatInputs[ current.code ] = current.value;
                return flatInputs;
            }, {} ),
            rawContentStates: getState().builder.inputs.reduce( ( flatInputs, current ) => {
                if ( current.rawContentState && Object.keys( current.rawContentState ).length > 0 ) { flatInputs[ current.code ] = current.rawContentState; }
                return flatInputs;
            }, {} )
        };

        fetch( `${ emailURL }/preview`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify( previewRequest )
        } )
        .then( response => response.json() )
        .then( async response => {
            if ( !response || !response.success ) {
                console.error( response.messages );
            } else {
                dispatch( loadPreview( response ) );
                dispatch( busyFetchingPreview( false ) );
            }
        } )
        .catch( ( error ) => {
            console.log( error );
            dispatch( busyFetchingPreview( false ) );
        } );

    };

};

/*******************************************************************************
 *  4. Reducers
 */

export default function appData( state = initialState, action ) {

    switch ( action.type ) {

        case DATA_LOADING:
            return {
                ...state,
                loading: action.loading
            };

        case DATA_LOADING_ERROR:
            return {
                ...state,
                loadingError: action.error,
                loading: false
            };

        case LOAD_DATA:
            return {
                ...state,
                ...action.data,
                configHasChanged: true
            };

        case LOAD_PREVIEW:
            return {
                ...state,
                preview: action.data.preview
            };

        case SAVE_EMAIL_RESPONSE:
        case UPDATE_SELECTED_EMAIL:
            return {
                ...state,
                configHasChanged: false
            };

        case ADD_EMAIL_COMPONENT:
        case UPDATE_EMAIL_PROPS:
        case UPDATE_INPUTS:
        case UPDATE_PROPS:
        case REMOVE_COMPONENT:
            return {
                ...state,
                configHasChanged: true
            };
        case SET_TOAST: {
            const updatedToasts = [ ...state.toasts ];
            if ( action.toastType && action.toastType.toUpperCase() === 'SUCCESS' ) {
                updatedToasts.push( { ...successToast, message: action.message } );
            } else if ( action.toastType && action.toastType.toUpperCase() === 'ERROR' ) {
                updatedToasts.push( { ...errorToast, message: action.message } );
            } else {
                updatedToasts.push( { ...infoToast, message: action.message } );
            }

            return {
                ...state,
                toasts: updatedToasts
            };
        }
        case CLEAR_TOAST: {
            return {
                ...state,
                toasts: []
            };
        }

        default:
            return state;
    }
}